import {
  AppAssistantActionType,
  AppAssistantQuickActionId,
  type AppAssistantQuickAction,
  type AppAssistantRequest,
  type AppAssistantResult,
  type AssistantLiveTokenResult,
  type AssistantUserStatus,
  type AssistantUserStatusRequest,
  type EscalateAssistantIssueRequest,
  type EscalateAssistantIssueResult,
  type MintAssistantLiveTokenRequest
} from '../../../../pure-js/types/AppAssistant'
import { AssistantAudience } from '../../../../pure-js/types/AssistantKnowledge'
import { isFacilitatorPathname } from '../../../../pure-js/libs/assistantNavigation'
import {
  AssistantActionLabelKey,
  AssistantMessageKey,
  assistantLabel,
  translatedMessage
} from '../../../../pure-js/libs/assistantMessages'
import type {
  MarkAssistantSupportRepliesReadRequest,
  ResolveAssistantSupportRequestRequest,
  SendAssistantSupportMessageRequest
} from '../../../../pure-js/types/AssistantEscalation'
import type { ApiResponse } from '../types'
import { loadCloudFunctionsHandler } from '../handlers'
import { buildMockLiveTokenConfig } from './mocks/coachMocks'
import { handleRequest } from './config'

export const askAppAssistant = async (request: AppAssistantRequest): Promise<ApiResponse<AppAssistantResult>> =>
  handleRequest(
    async (): Promise<ApiResponse<AppAssistantResult>> => ({
      data: buildLocalAssistantResult(request),
      success: true
    }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.askAppAssistant(request)
    }
  )

export const getAssistantUserStatus = async (
  request: AssistantUserStatusRequest
): Promise<ApiResponse<AssistantUserStatus | undefined>> =>
  handleRequest(
    async (): Promise<ApiResponse<AssistantUserStatus | undefined>> => ({
      // Mock mode has no live session/sprint state; report a benign "ready" status.
      data: {
        role: AssistantAudience.GAMER,
        belongsToMultipleOrganizations: false,
        latestSession: { sessionId: 'mock', analysisInProgress: false, feedbackReady: true }
      },
      success: true
    }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.getAssistantUserStatus(request)
    }
  )

export const mintAssistantLiveToken = async (
  request: MintAssistantLiveTokenRequest
): Promise<ApiResponse<AssistantLiveTokenResult>> =>
  handleRequest(
    // Paired with StubRealtimeTransport — the token is never sent anywhere.
    async (): Promise<ApiResponse<AssistantLiveTokenResult>> => ({
      data: { config: buildMockLiveTokenConfig() },
      success: true
    }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.mintAssistantLiveToken(request)
    }
  )

export const escalateAssistantIssue = async (
  request: EscalateAssistantIssueRequest
): Promise<ApiResponse<EscalateAssistantIssueResult>> =>
  handleRequest<ApiResponse<EscalateAssistantIssueResult>>(
    async () => ({ data: { delivered: true }, success: true }),
    async () => {
      const cloudFunctions = await loadCloudFunctionsHandler()
      return cloudFunctions.escalateAssistantIssue(request)
    }
  )

export const sendAssistantSupportMessage = async (request: SendAssistantSupportMessageRequest) =>
  handleRequest(
    async () => ({ data: { sent: true }, success: true }),
    async () => (await loadCloudFunctionsHandler()).sendAssistantSupportMessage(request)
  )

export const resolveAssistantSupportRequest = async (request: ResolveAssistantSupportRequestRequest) =>
  handleRequest(
    async () => ({ data: { resolved: true }, success: true }),
    async () => (await loadCloudFunctionsHandler()).resolveAssistantSupportRequest(request)
  )

export const markAssistantSupportRepliesRead = async (request: MarkAssistantSupportRepliesReadRequest) =>
  handleRequest(
    async () => ({ data: { marked: 0 }, success: true }),
    async () => (await loadCloudFunctionsHandler()).markAssistantSupportRepliesRead(request)
  )

function buildLocalAssistantResult(request: AppAssistantRequest): AppAssistantResult {
  const isFacilitator = isFacilitatorPathname(request.context?.pathname ?? '')
  const capabilities = isFacilitator
    ? AssistantMessageKey.CAPABILITIES_FACILITATOR
    : AssistantMessageKey.CAPABILITIES_GAMER

  return {
    message: translatedMessage(capabilities),
    action: { type: AppAssistantActionType.ANSWER, text: '' },
    quickActions: isFacilitator ? MOCK_FACILITATOR_QUICK_ACTIONS : MOCK_GAMER_QUICK_ACTIONS
  }
}

// Mock mode has no sprint data, so the chips mirror the live ones without any target ids.
const MOCK_FACILITATOR_QUICK_ACTIONS: AppAssistantQuickAction[] = [
  {
    id: AppAssistantQuickActionId.SELECT_REPORT_SPRINT,
    label: assistantLabel(AssistantActionLabelKey.QUICK_SELECT_REPORT_SPRINT)
  },
  {
    id: AppAssistantQuickActionId.SELECT_PARTICIPANT_PERFORMANCE,
    label: assistantLabel(AssistantActionLabelKey.QUICK_SELECT_PARTICIPANT)
  },
  { id: AppAssistantQuickActionId.CONTROL_TOWER, label: assistantLabel(AssistantActionLabelKey.QUICK_CONTROL_TOWER) }
]

const MOCK_GAMER_QUICK_ACTIONS: AppAssistantQuickAction[] = [
  {
    id: AppAssistantQuickActionId.SELF_SESSION_PERFORMANCE,
    label: assistantLabel(AssistantActionLabelKey.QUICK_SELF_SESSION_PERFORMANCE)
  },
  {
    id: AppAssistantQuickActionId.SELF_SESSION_NEXT_STEP,
    label: assistantLabel(AssistantActionLabelKey.QUICK_SELF_SESSION_NEXT_STEP)
  },
  {
    id: AppAssistantQuickActionId.LATEST_FEEDBACK,
    label: assistantLabel(AssistantActionLabelKey.QUICK_LATEST_FEEDBACK)
  },
  {
    id: AppAssistantQuickActionId.UPCOMING_ACTIVITIES,
    label: assistantLabel(AssistantActionLabelKey.QUICK_UPCOMING_ACTIVITIES)
  }
]
